import { useContext, useMemo, useState } from "react";
import {
  GroupType,
  SignalsGroupsType,
} from "@shared/features/onboarding/types";
import {
  LanguageNameSpaces,
  MarketplaceSubGroup,
  WorkspaceType,
} from "@shared/types";
import { useSelector } from "react-redux";
import { sharedLib } from "@shared/services/shared.service";
import { GroupsPageContext } from "@shared/features/groups/GroupsPage.context";
import { selectWorkspace } from "@shared/slices/common.slice";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";

const useSignalsGroupsSection = <T extends GroupType>(
  groupType: SignalsGroupsType,
  groups?: T[],
  isSuccess?: boolean,
  mobileSearchValue?: string
) => {
  const {
    noExternalAccountConnected: baseNoExternalAccountConnected,
    isGroupsPage,
    refetch,
  } = useContext(GroupsPageContext)!;
  const { t } = useTypedTranslation(LanguageNameSpaces.GROUPS);
  const workspace = useSelector(selectWorkspace);
  const user = useSelector(sharedLib.selectors.authentication.selectUser);
  const [scoreModalGroup, setScoreModalGroup] = useState<MarketplaceSubGroup>();
  const [searchValue, setSearchValue] = useState("");
  const searchValueToUse = mobileSearchValue ?? searchValue;

  const isMyGroups = groupType === SignalsGroupsType.MY_GROUPS;
  const isPersonalGroups = groupType === SignalsGroupsType.PERSONAL;
  const isFreeTrialGroups = groupType === SignalsGroupsType.FREE_TRIAL;
  const noExternalAccountConnected =
    baseNoExternalAccountConnected && isMyGroups;

  const scoreModalProps = {
    group: scoreModalGroup,
    open: !!scoreModalGroup,
    closeModal: () => setScoreModalGroup(undefined),
  };

  const emptyStateTitle = isFreeTrialGroups
    ? t("empty.free_trial_no_match", {
        workspace: t(
          workspace === WorkspaceType.DEMO ? "common.demo" : "common.exchange"
        ),
      })
    : t("empty.no_group_type", { groupType: t(groupType) });

  const filteredGroups = useMemo(() => {
    return groups?.filter((group) =>
      group.name.toLowerCase().includes(searchValueToUse.toLowerCase())
    );
  }, [groups, searchValueToUse]);

  const myGroupsEmpty = isMyGroups && !filteredGroups?.length;
  const noGroupsFound =
    isSuccess &&
    myGroupsEmpty &&
    (user?.hasExternalAccount() || user?.is_whop_user);
  const showEmptyState =
    (isMyGroups && user && (noExternalAccountConnected || noGroupsFound)) ||
    (!isMyGroups && isSuccess && !filteredGroups?.length);

  return {
    filteredGroups,
    isMyGroups,
    isPersonalGroups,
    noExternalAccountConnected,
    noGroupsFound,
    showEmptyState,
    scoreModalProps,
    setScoreModalGroup,
    isGroupsPage,
    refetch,
    emptyStateTitle,
    searchValue,
    setSearchValue,
    searchValueToUse,
    isFreeTrialGroups,
  };
};

export default useSignalsGroupsSection;
